import { AnalyzeSitewideFrontendTinybirdRequestBodyType } from "@intelligems/ig-types/schemas/analytics/analyze-sitewide";
import {
  ConversionFunnelEntryPoint,
  DatasetId,
  ExternalSiteWideAnalyticsAudience,
} from "@intelligems/ig-types/schemas/analytics/base";
import { ExternalSitewideAnalyticsGranularity } from "@intelligems/ig-types/functions/analytics/external-get-sitewide-analytics";
import { SitewideAnalyticsFiltersType } from "@intelligems/ig-types/schemas/analytics/filters";
import { SitewideInterval } from "@intelligems/ig-types/dtos/sitewide-analytics";

const EMPTY_URL_PARAM: NonNullable<SitewideAnalyticsFiltersType["urlParam"]> = {
  queryParam: null,
  queryParamValue: null,
  queryParamFilterType: null,
};

const EMPTY_LANDING_PAGE: NonNullable<
  SitewideAnalyticsFiltersType["landingPage"]
> = {
  landingPageUrlPath: null,
  landingPageUrlPathFilterType: null,
};

const EMPTY_USER_BEHAVIOR: NonNullable<
  SitewideAnalyticsFiltersType["userBehavior"]
> = {
  viewedCheckout: null,
  addedToCart: null,
  viewedProductPage: null,
  viewedCollectionPage: null,
};

export function getEmptyAnalyticsUrlParam() {
  return { ...EMPTY_URL_PARAM };
}

export function getEmptyAnalyticsLandingPage() {
  return { ...EMPTY_LANDING_PAGE };
}

export function getEmptyAnalyticsUserBehavior() {
  return { ...EMPTY_USER_BEHAVIOR };
}

export function getDefaultSitewideFilters(
  filters?: Partial<SitewideAnalyticsFiltersType> | null
): SitewideAnalyticsFiltersType {
  return {
    deviceType: filters?.deviceType ?? "any",
    visitorType: filters?.visitorType ?? "any",
    customerType: filters?.customerType ?? null,
    sourceSitesOrChannels: filters?.sourceSitesOrChannels ?? [],
    countryCodes: filters?.countryCodes ?? [],
    countryCodesFilterType: filters?.countryCodesFilterType ?? "include",
    currencyCodes: filters?.currencyCodes ?? [],
    currencyCodesFilterType: filters?.currencyCodesFilterType ?? "include",
    maxZScore: filters?.maxZScore ?? "9999",
    urlParam: filters?.urlParam ?? getEmptyAnalyticsUrlParam(),
    landingPage: filters?.landingPage ?? getEmptyAnalyticsLandingPage(),
    landingPageFilters: filters?.landingPageFilters,
    landingPageFiltersOperator: filters?.landingPageFiltersOperator,
    landingPageTypes: filters?.landingPageTypes,
    userBehavior: filters?.userBehavior ?? getEmptyAnalyticsUserBehavior(),
    triggeredCustomEvents: filters?.triggeredCustomEvents ?? [],
    customEventsFilterType: filters?.customEventsFilterType ?? undefined,
    reachedCheckoutStage: filters?.reachedCheckoutStage ?? null,
    minNetRevenue: filters?.minNetRevenue ?? null,
    maxNetRevenue: filters?.maxNetRevenue ?? null,
    minShippingRevenue: filters?.minShippingRevenue ?? null,
    maxShippingRevenue: filters?.maxShippingRevenue ?? null,
    shippingMethods: filters?.shippingMethods ?? [],
    onlyProductIds: filters?.onlyProductIds ?? [],
  };
}

const ALL_ORDERS_OVERRIDES: Partial<SitewideAnalyticsFiltersType> = {
  deviceType: "any",
  visitorType: "any",
  sourceSitesOrChannels: [],
  countryCodes: [],
  countryCodesFilterType: "include",
  triggeredCustomEvents: [],
  customEventsFilterType: "any",
  reachedCheckoutStage: null,
};

export function applyAllOrdersOverride(
  filters: SitewideAnalyticsFiltersType
): SitewideAnalyticsFiltersType {
  return {
    ...filters,
    ...ALL_ORDERS_OVERRIDES,
    urlParam: getEmptyAnalyticsUrlParam(),
    landingPage: getEmptyAnalyticsLandingPage(),
    userBehavior: getEmptyAnalyticsUserBehavior(),
  };
}

export interface BuildSitewidePayloadOptions {
  filters?: Partial<SitewideAnalyticsFiltersType> | null;
  startTs: string | number | null;
  endTs: string | number | null;
  datasetId: DatasetId;
  interval?: string | null;
  allOrders?: boolean;
  comparisonStartTs?: string | number | null;
  comparisonEndTs?: string | number | null;
  audience?: ExternalSiteWideAnalyticsAudience | undefined;
  // Conversion-funnel-only step-1 pivot. Distinct from `audience` so the funnel
  // sends its own wire key (`entry_point`) while snapshot/timeseries send
  // `audience`. Only the funnel controller sets this.
  entryPoint?: ConversionFunnelEntryPoint | undefined;
  holidayBenchmarking?: Record<string, unknown>;
}

const GranularityMap: Record<
  ExternalSitewideAnalyticsGranularity,
  SitewideInterval
> = {
  day: "1 DAY",
  week: "1 WEEK",
  month: "1 MONTH",
};

export function coerceSitewideAnalyticsGranularity(
  granularity?: ExternalSitewideAnalyticsGranularity
): SitewideInterval {
  return granularity ? (GranularityMap[granularity] ?? "1 WEEK") : "1 WEEK";
}

export function buildSitewideAnalyticsPayload(
  options: BuildSitewidePayloadOptions
): AnalyzeSitewideFrontendTinybirdRequestBodyType {
  const defaultedFilters = getDefaultSitewideFilters(options.filters);
  const filters = options.allOrders
    ? applyAllOrdersOverride(defaultedFilters)
    : defaultedFilters;

  return {
    deviceType: filters.deviceType ?? "any",
    visitorType: filters.visitorType ?? "any",
    customerType: filters.customerType ?? null,
    sourceSitesOrChannels: filters.sourceSitesOrChannels ?? [],
    countryCodes: filters.countryCodes ?? [],
    countryCodesFilterType: filters.countryCodesFilterType ?? "include",
    currencyCodes: filters.currencyCodes ?? [],
    currencyCodesFilterType: filters.currencyCodesFilterType ?? "include",
    maxZScore: filters.maxZScore ?? "9999",
    urlParam: filters.urlParam ?? getEmptyAnalyticsUrlParam(),
    landingPage: filters.landingPage ?? getEmptyAnalyticsLandingPage(),
    userBehavior: filters.userBehavior ?? getEmptyAnalyticsUserBehavior(),
    triggeredCustomEvents: filters.triggeredCustomEvents ?? [],
    customEventsFilterType: filters.customEventsFilterType ?? "any",
    landingPageFilters: filters.landingPageFilters?.length
      ? filters.landingPageFilters
      : undefined,
    landingPageFiltersOperator: filters.landingPageFilters?.length
      ? (filters.landingPageFiltersOperator ?? "or")
      : undefined,
    landingPageTypes: filters.landingPageTypes ?? undefined,
    reachedCheckoutStage: filters.reachedCheckoutStage ?? null,
    minNetRevenue: filters.minNetRevenue ?? null,
    maxNetRevenue: filters.maxNetRevenue ?? null,
    minShippingRevenue: filters.minShippingRevenue ?? null,
    maxShippingRevenue: filters.maxShippingRevenue ?? null,
    shippingMethods: filters.shippingMethods ?? [],
    onlyProductIds: filters.onlyProductIds ?? [],
    resourceType: "sitewide",
    audience: options.audience,
    entryPoint: options.entryPoint,
    datasetId: options.datasetId,
    startTs: options.startTs,
    endTs: options.endTs,
    allOrders: options.allOrders ?? false,
    shippingExpensePerOrder: null,
    transactionFeePct: null,
    comparisonStartTs: options.comparisonStartTs ?? null,
    comparisonEndTs: options.comparisonEndTs ?? null,
    interval: options.interval ?? null,
    holidayBenchmarking: options.holidayBenchmarking ?? {},
  };
}
